import React, { useRef } from "react";
import { LucideBookText } from "lucide-react";
import Text from "@src/lib/ui/Text";
import { cn } from "@src/lib/utils/cn";
import { OpusAgentErrorBubble } from "./OpusAgentErrorBubble";
import { OpusAgentTurnMessage } from "./OpusAgentTurnMessage";
import { useStickToBottom } from "./useStickToBottom";
import type { OpusAgentTurnState } from "./useOpusAgentChat";

interface OpusAgentThreadProps {
  turns: OpusAgentTurnState[];
  loading: boolean;
  loadFailed: boolean;
  // The drawer's scroll container (the thread is its content).
  scrollerRef: React.RefObject<HTMLDivElement | null>;
  // Identity of the conversation being shown; a change re-pins to the bottom.
  chatKey: string | null;
  onSendMessage: (message: string) => void;
  onResumePolling: (turnId: string) => void;
  onRetryLoad: () => void;
  onEditMessage?: (turnId: string, newMessage: string) => Promise<boolean>;
}

// Example prompts for the empty state: each fills and sends. Always shown on
// the agent surface — the KB chat's generated SuggestedQuestions (and its
// flag) describe library lookups, not what the agent can do.
export const OPUS_AGENT_EXAMPLE_PROMPTS: readonly string[] = [
  "Which locations have the lowest course completion this month?",
  "Who hasn't finished onboarding yet?",
  "What does our training say about closing procedures?",
  "Give me a health check of our training program.",
];

// Layout duplicated from the Ask Opus ChatThread / KnowledgeBaseChatSidebar
// empty state, driven by polled turns instead of streamed messages.
export const OpusAgentThread: React.FC<OpusAgentThreadProps> = ({
  turns,
  loading,
  loadFailed,
  scrollerRef,
  chatKey,
  onSendMessage,
  onResumePolling,
  onRetryLoad,
  onEditMessage,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  // Opens and chat switches paint already scrolled to the bottom; incoming
  // events keep the view pinned unless the reader scrolled up.
  useStickToBottom({ scrollerRef, contentRef, resetKey: chatKey });

  const isEmpty = turns.length === 0 && !loading && !loadFailed;

  return (
    // pt-4 keeps the first element (whatever it is) off the drawer header,
    // mirroring the h-4 spacer that ends the thread.
    <div ref={contentRef} className="flex h-full min-w-0 flex-col pt-4">
      <div
        className={cn(
          "flex min-h-0 min-w-0 flex-1 flex-col",
          isEmpty && "justify-center",
        )}
      >
        <div
          className={cn(
            "flex w-full min-w-0 max-w-full flex-col",
            isEmpty ? "items-center gap-1" : "gap-1",
          )}
        >
          {isEmpty && (
            <div
              className="flex w-full max-w-full min-w-0 flex-col items-center justify-center px-2 py-16"
              data-testid="opus-agent-empty-state"
            >
              <Text variant="body-title" className="text-center">
                Hi! I&apos;m Ask Opus.
              </Text>
              <Text
                variant="compact-title"
                className="mt-1 text-center text-text-secondary"
                multiline
              >
                I can dig into your training data, search your library, and
                explain how things work.
              </Text>
              <div
                className="mt-6 flex w-full max-w-full min-w-0 flex-col gap-1.5"
                data-testid="opus-agent-example-prompts"
              >
                {OPUS_AGENT_EXAMPLE_PROMPTS.map((prompt) => (
                  <button
                    key={prompt}
                    type="button"
                    data-testid="opus-agent-example-prompt"
                    onClick={() => onSendMessage(prompt)}
                    className="w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-left transition-colors hover:bg-state-hover focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none"
                  >
                    <Text
                      variant="fine-print-title"
                      className="min-w-0 text-text-primary"
                      multiline
                    >
                      {prompt}
                    </Text>
                  </button>
                ))}
              </div>
              <div className="mt-6 flex min-w-0 items-start gap-1 px-2">
                <LucideBookText className="mt-0.5 size-[14px] shrink-0 text-icon-secondary" />
                <Text
                  variant="fine-print-title"
                  className="min-w-0 text-text-secondary"
                  multiline
                >
                  Answers draw on your Opus library and your organization&apos;s
                  data.
                </Text>
              </div>
            </div>
          )}
          {loadFailed && (
            <OpusAgentErrorBubble
              message="Could not load this conversation."
              onRetry={onRetryLoad}
            />
          )}
          {turns.map((turn, index) => (
            <OpusAgentTurnMessage
              key={turn.id}
              turn={turn}
              turnIndex={index}
              onRetry={onSendMessage}
              onResumePolling={onResumePolling}
              onEditMessage={onEditMessage}
            />
          ))}
          <div className="h-4" />
        </div>
      </div>
    </div>
  );
};
